import {
  BackSide,
  BoxGeometry,
  CircleGeometry,
  CylinderGeometry,
  Matrix4, // PlaneGeometry,
  Quaternion,
  Vector3,
} from 'three';
import type {
  Box3,
  BoxHelper,
  BufferGeometry,
  Float32BufferAttribute,
  Sphere,
  Uint16BufferAttribute,
} from 'three';

import { wait } from '@frontend/utils';

import ViewPort from '../ViewPort';
import { COLORS, PART_TYPES, STOCK_TYPES, UNITS } from '../constants';
import type { VectorJson, PointJson } from '../contracts/basic';
import type { TessJson, FaceJson, EdgeJson } from '../contracts/tess';
import { getMidpoint, isClose, isCloseVectors } from '../geom-utils';
import Assembly from '../loaders/assembly';
import AssemblyInstance from '../loaders/assemblyInstance';
import Body from '../loaders/body';
import type { AnyBody } from '../loaders/body';
import Part from '../loaders/part';
import type { AnyPart } from '../loaders/part';
import {
  STEPGeometry,
  sphereToSphereJson,
  boxToBoxJson,
  type GeometryData,
  type FaceMetaData,
} from '../loaders/step/STEPGeometry';
import { convert } from '../utils';
import { mergeGeometries } from '../utils/BufferGeometryUtils';
import { prepareBoundBoxForBodies } from './boundbox-utils';
import { setUpCameraAndControls } from './control-utils';
import { setVisibilityForObjectIds } from './focus-utils';
import type { Renderer, RendererState } from './renderer';

const STOCK_OPACITY = 0.5;
const VISUALIZATION_GAP_FRACTION = 0.15;
const WORKPIECE_COLOR = COLORS.green;
const SACRIFICIAL_COLOR = COLORS.red;
const UNUSED_COLOR = COLORS.blue;
const BAR_END_COLOR = COLORS.cyan;

type CylindericalOrRectangularResultBase = {
  barEnd: number;
  bodyId: string;
  buffer: number;
  key: string;
  length: number;
  sacrificialWidth: number;
};

type RawCylindericalOrRectangularResult =
  CylindericalOrRectangularResultBase & {
    center?: VectorJson;
    direction?: VectorJson; //defined if lengthDirection is not defined
    lengthDirection?: VectorJson;
    heightDirection?: VectorJson;
    widthDirection?: VectorJson;
  };

type CylindericalOrRectangularResult = CylindericalOrRectangularResultBase & {
  center?: Vector3;
  lengthDirection?: Vector3;
  heightDirection?: Vector3;
  widthDirection?: Vector3;
};

type CylindericalStockOptions = {
  diameter: number;
  length: number;
  makes: number;
  id: string;
  quantity: number;
};
type CylindercalResultBase = {
  radius: number;
  stockOptions: CylindericalStockOptions[];
};

type RawCylindercalResult = CylindercalResultBase &
  RawCylindericalOrRectangularResult;

type CylindercalResult = CylindercalResultBase &
  CylindericalOrRectangularResult;

type RectangularStockOptions = {
  length: number;
  width: number;
  height: number;
  makes: number;
  id: string;
  quantity: number;
};
type RectangularResultBase = {
  height: number;
  stockOptions: RectangularStockOptions[];
  width: number;
};

type RawRectangularResult = RectangularResultBase &
  RawCylindericalOrRectangularResult;

type RectangularResult = RectangularResultBase &
  CylindericalOrRectangularResult;

type RawSheetResult = {
  transformsInfo: {
    transforms: number[][];
    //eslint-disable-next-line camelcase
    sheet_points: [PointJson, ...PointJson[]]; //1 or more (perhaps 2 or more)
    //eslint-disable-next-line camelcase
    part_points: [PointJson, ...PointJson[]]; //1 or more (perhaps 2 or more)
  };
  partsPerSheet: number;
  quantity: number;
  sheetCount: number;
};

type SheetResult = {
  transforms: Matrix4[];
  sheetPoints: [Vector3, ...Vector3[]]; //1 or more (perhaps 2 more)
  partPoints: [Vector3, ...Vector3[]]; //1 or more (perhaps 2 or more)
  partsPerSheet: number;
  quantity: number;
  sheetCount: number;
};

type ResultForCreationBase<T extends STOCK_TYPES> = {
  name: string;
  quantity: number;
  stockType: T;
  key: string;
  bodyId: string;
  tessellation: TessJson; //for DXF OR STEP
  useBaseGeometry?: boolean;
  isManual?: boolean;
} & (
  | {
      partUuid: string;
      uuid?: undefined;
      partFileUuid?: undefined;
    }
  | {
      partUuid?: undefined;
      uuid: string; //uuid is preferred
      partFileUuid?: undefined;
    }
  | {
      partUuid?: undefined;
      uuid?: undefined;
      partFileUuid: string;
    }
);

export type RawResultForCreation<T extends STOCK_TYPES> =
  ResultForCreationBase<T> &
    (T extends STOCK_TYPES.CYLINDRICAL
      ? RawCylindercalResult
      : T extends STOCK_TYPES.RECTANGULAR
      ? RawRectangularResult
      : T extends STOCK_TYPES.SHEET
      ? RawSheetResult
      : never);

export type ResultForCreation<T extends STOCK_TYPES> =
  ResultForCreationBase<T> &
    (T extends STOCK_TYPES.CYLINDRICAL
      ? CylindercalResult
      : T extends STOCK_TYPES.RECTANGULAR
      ? RectangularResult
      : T extends STOCK_TYPES.SHEET
      ? SheetResult
      : never);

// type RectangularResultOrCylindericalResult = ResultForCreation<STOCK_TYPES.CYLINDRICAL> | ResultForCreation<STOCK_TYPES.RECTANGULAR>;

const convertPropertiesIfNeeded = <
  T extends Record<string, unknown>,
  K extends keyof T,
>(
  o: T,
  keys: K[],
  needsConversion: boolean,
): Pick<T, K> =>
  Object.fromEntries(
    keys.map((key) => [
      key,
      needsConversion
        ? convert(o[key] as number, UNITS.IN, UNITS.MM, 1)
        : o[key],
    ]),
  ) as Pick<T, K>;

/*
Reconciles native units of file against the result, which will always come in in MM.
Also converts object vectors into THREE.JS vectors
*/
const prepareResultForCreation = <T extends STOCK_TYPES>(
  result: RawResultForCreation<T>,
  nativeUnits: UNITS,
): ResultForCreation<T> => {
  const copy = JSON.parse(JSON.stringify(result)) as ResultForCreation<T>;
  const { stockType } = result;

  if (
    stockType === STOCK_TYPES.RECTANGULAR ||
    stockType === STOCK_TYPES.CYLINDRICAL
  ) {
    // first convert
    const needsConversion = nativeUnits === UNITS.IN;
    const lengthDirection =
      (result as unknown as RawCylindericalOrRectangularResult)
        .lengthDirection ||
      (result as unknown as RawCylindericalOrRectangularResult).direction; //is there ever result.direction?
    const widthDirection = (
      result as unknown as RawCylindericalOrRectangularResult
    ).widthDirection;
    const heightDirection = (
      result as unknown as RawCylindericalOrRectangularResult
    ).heightDirection;
    const center = (result as unknown as RawCylindericalOrRectangularResult)
      .center;
    Object.assign(copy, {
      ...convertPropertiesIfNeeded(
        result as unknown as RawCylindericalOrRectangularResult,
        ['sacrificialWidth', 'length', 'buffer', 'barEnd'],
        needsConversion,
      ),
      ...(center && {
        center: new Vector3(
          ...Object.values(
            convertPropertiesIfNeeded(center, ['x', 'y', 'z'], needsConversion),
          ),
        ),
      }),
      ...(stockType === STOCK_TYPES.CYLINDRICAL
        ? {
            ...convertPropertiesIfNeeded(
              result as unknown as CylindercalResultBase,
              ['radius'],
              needsConversion,
            ),
            stockOptions: (
              result as unknown as CylindercalResultBase
            ).stockOptions.map((options) => ({
              ...options,
              ...convertPropertiesIfNeeded(
                options,
                ['diameter', 'length'],
                needsConversion,
              ),
            })),
          }
        : {
            ...convertPropertiesIfNeeded(
              result as unknown as RectangularResultBase,
              ['width', 'height'],
              needsConversion,
            ),
            stockOptions: (
              result as unknown as RectangularResultBase
            ).stockOptions.map((options) => ({
              ...options,
              ...convertPropertiesIfNeeded(
                options,
                ['length', 'width', 'height'],
                needsConversion,
              ),
            })),
          }),
      //Note: conversions are not required for directions
      ...(lengthDirection && {
        lengthDirection: new Vector3(
          lengthDirection.x,
          lengthDirection.y,
          lengthDirection.z,
        ),
      }),
      ...(widthDirection && {
        widthDirection: new Vector3(
          widthDirection.x,
          widthDirection.y,
          widthDirection.z,
        ),
      }),
      ...(heightDirection && {
        heightDirection: new Vector3(
          heightDirection.x,
          heightDirection.y,
          heightDirection.z,
        ),
      }),
    } as ResultForCreation<STOCK_TYPES.RECTANGULAR | STOCK_TYPES.CYLINDRICAL>);
  } else if (stockType === STOCK_TYPES.SHEET) {
    Object.assign(copy, {
      transforms: (
        result as unknown as RawSheetResult
      ).transformsInfo.transforms.map((trnsfm) =>
        new Matrix4().fromArray(trnsfm),
      ),
      sheetPoints: (
        result as unknown as RawSheetResult
      ).transformsInfo.sheet_points.map(
        (pnt) => new Vector3(pnt[0], pnt[1], pnt[2]),
      ),
      partPoints: (
        result as unknown as RawSheetResult
      ).transformsInfo.part_points.map(
        (pnt) => new Vector3(pnt[0], pnt[1], pnt[2]),
      ),
    } as ResultForCreation<STOCK_TYPES.SHEET>);
  }

  return copy;
};

type GeneratedGeometry<T extends BufferGeometry> = T & {
  meta?: FaceJson['meta'];
};

const cylinderAndCircleSegments = 64;

// NOTE: below generates cylinders with 3 faces, this is worse for performance
// but perhaps better for experience allowing for caliper stuff,
// but as that is disabled anyways, we will result to the simpler method of just
// returning one geometry for our step faces, as its simpler and better for
// performance
const generateCylinderFaceGeometries = (
  length: number,
  diameter: number,
): [
  GeneratedGeometry<CircleGeometry>,
  GeneratedGeometry<CircleGeometry>,
  GeneratedGeometry<CylinderGeometry>,
] => {
  const bottom = new CircleGeometry(
    diameter / 2,
    cylinderAndCircleSegments,
  ) as CircleGeometry & {
    meta: FaceJson['meta'];
  };
  bottom.rotateX(Math.PI / 2);
  bottom.translate(0, -length / 2, 0);
  bottom.meta = {
    type: 'plane',
    axis: [0, -1, 0],
    center: [0, -length / 2, 0],
    length: diameter,
    width: diameter,
  };
  const top = new CircleGeometry(
    diameter / 2,
    cylinderAndCircleSegments,
  ) as CircleGeometry & {
    meta: FaceJson['meta'];
  };
  top.rotateX(-Math.PI / 2);
  top.translate(0, length / 2, 0);
  top.meta = {
    type: 'plane',
    axis: [0, 1, 0],
    center: [0, length / 2, 0],
    length: diameter,
    width: diameter,
  };
  const cylinder = new CylinderGeometry(
    diameter / 2,
    diameter / 2,
    length,
    cylinderAndCircleSegments,
    1,
    true, // openEnded flag
  ) as CylinderGeometry & { meta: FaceJson['meta'] };
  cylinder.meta = {
    type: 'cylinder',
    axis: [0, 1, 0],
    center: [0, 0, 0],
    height: length,
    angle: 2 * Math.PI,
    diameter: diameter,
  };
  cylinder.normalizeNormals();
  cylinder.computeVertexNormals(); //Make the faces look smoother
  return [bottom, top, cylinder];
};

// TODO: get this working, going to be a translation nightmare,
// we are going to with the single face strategy for now
// NOTE: below generates individual face geometries for each face
// of the prism. Worse for performance but better for experience
// rolling with single face strategy for now.

const generateRectangleBoxGeometry = (
  length: number,
  width: number,
  height: number,
): [BoxGeometry] => {
  const box = new BoxGeometry(width, height, length);
  return [box];
};

const generateBarFaceGeometries = (
  stockType: STOCK_TYPES.CYLINDRICAL | STOCK_TYPES.RECTANGULAR,
  length: number,
  stockOption: CylindericalStockOptions | RectangularStockOptions,
): GeneratedGeometry<BufferGeometry>[] | undefined =>
  stockType === STOCK_TYPES.CYLINDRICAL
    ? generateCylinderFaceGeometries(
        length,
        (stockOption as CylindericalStockOptions).diameter,
      )
    : stockType === STOCK_TYPES.RECTANGULAR
    ? generateRectangleBoxGeometry(
        length,
        (stockOption as RectangularStockOptions).width,
        (stockOption as RectangularStockOptions).height,
      )
    : undefined;

const createGeometryDataFromGeometries = (
  geometries?: GeneratedGeometry<BufferGeometry>[],
): GeometryData => {
  if (geometries && geometries.length) {
    const mergedGeometry = mergeGeometries(geometries);
    const {
      position: positions,
      normal: normals,
      uv: uvs,
    } = mergedGeometry.attributes as {
      position: Float32BufferAttribute;
      normal: Float32BufferAttribute;
      uv?: Float32BufferAttribute;
    };
    const indices = mergedGeometry.index as Uint16BufferAttribute;
    const faceGeometries: GeometryData['faceGeometries'] = [
      {
        ...(positions && { positions }),
        ...(normals && { normals }),
        ...(uvs && { uvs }),
        ...(indices && { indices }),
      },
    ];
    const faceMetaData: FaceMetaData[] = [];
    let bufStart = 0;
    for (const [index, geometry] of geometries.entries()) {
      const bufCount =
        geometry.attributes.position.count *
        geometry.attributes.position.itemSize;
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      faceMetaData.push({
        ...geometry.meta,
        index,
        bufferRanges: [
          {
            bufNum: 0,
            bufStart,
            bufCount,
            boundBox: boxToBoxJson(geometry.boundingBox as Box3),
            boundSphere: sphereToSphereJson(geometry.boundingSphere as Sphere),
          },
        ],
        boundBox: boxToBoxJson(geometry.boundingBox as Box3),
        boundSphere: sphereToSphereJson(geometry.boundingSphere as Sphere),
      });
      bufStart += bufCount;
    }
    return {
      faceGeometries,
      faceMetaData,
    };
  }
  return {};
};

const createStepGeometryFromManualFaces = (
  geometry: GeometryData,
): STEPGeometry =>
  new STEPGeometry(
    {
      Faces: [],
      Edges: [],
      Vertexes: [],
      Type: PART_TYPES.STEP,
    },
    undefined, //no progress
    undefined, //no AbortSignal
    geometry,
  );

const createStockPart = (
  name: string,
  id: string,
  geometry: STEPGeometry,
  renderer: Renderer,
): Part<PART_TYPES.STEP> => {
  const newPart = new Part<PART_TYPES.STEP>(
    {
      partFileUuid: id,
      name,
      tess: {
        Type: PART_TYPES.STEP,
        Faces: [],
        Edges: [],
        Vertexes: [],
      },
    },
    renderer,
    undefined, //no parentProgress
    undefined, //no AbortSignal
    geometry,
  );
  newPart.setBoxBounds();
  return newPart;
};

const getBaseCylindricalTransform = (
  { lengthDirection, center }: ResultForCreation<STOCK_TYPES.CYLINDRICAL>,
  length: number,
): Matrix4 => {
  const baseTransform = new Matrix4();
  const up = new Vector3(0, 1, 0);
  const positionSet = new Vector3().addVectors(
    center as Vector3,
    new Vector3().copy(lengthDirection as Vector3).multiplyScalar(length / 2),
  );
  // apply buffer offset now that its the right spot
  if (!isCloseVectors(up, lengthDirection)) {
    const rotAxis = new Vector3()
      .crossVectors(lengthDirection as Vector3, up)
      .normalize();
    const angle = up.angleTo(lengthDirection as Vector3);
    baseTransform.makeRotationFromQuaternion(
      new Quaternion().setFromAxisAngle(rotAxis, -angle),
    );
  }
  baseTransform.setPosition(positionSet);
  return baseTransform;
};

export const getBaseRectangularTransform = (
  {
    lengthDirection,
    widthDirection,
    heightDirection,
    center,
  }: ResultForCreation<STOCK_TYPES.RECTANGULAR>,
  length: number,
): Matrix4 => {
  // https://threejs.org/docs/#api/en/math/Matrix4.makeBasis
  const baseTransform = new Matrix4().makeBasis(
    widthDirection as Vector3,
    heightDirection as Vector3,
    lengthDirection as Vector3,
  );
  const positionSet = new Vector3().addVectors(
    center as Vector3,
    new Vector3().copy(lengthDirection as Vector3).multiplyScalar(length / 2),
  );
  baseTransform.setPosition(positionSet);
  return baseTransform;
};

const getBaseTransform = (
  result: ResultForCreation<STOCK_TYPES>,
  length: number,
  bodyTransform: Matrix4,
): Matrix4 => {
  let transform: Matrix4;
  if (result.stockType === STOCK_TYPES.CYLINDRICAL) {
    transform = getBaseCylindricalTransform(
      result as ResultForCreation<STOCK_TYPES.CYLINDRICAL>,
      length,
    );
  } else {
    transform = getBaseRectangularTransform(
      result as ResultForCreation<STOCK_TYPES.RECTANGULAR>,
      length,
    );
  }
  transform.premultiply(bodyTransform);
  return transform;
};

const getOffsetDirectionForNthBar = (
  result:
    | ResultForCreation<STOCK_TYPES.CYLINDRICAL>
    | ResultForCreation<STOCK_TYPES.RECTANGULAR>,
): Vector3 | undefined => {
  const { lengthDirection } = result;
  if (result.stockType === 'cylindrical') {
    const crosser = new Vector3(0, 0, 1);
    if (!isCloseVectors(crosser, lengthDirection)) {
      return new Vector3()
        .crossVectors(crosser, lengthDirection as Vector3)
        .normalize();
    } else {
      return new Vector3()
        .crossVectors(new Vector3(1, 0, 0), lengthDirection as Vector3)
        .normalize();
    }
  } else if (result.stockType === STOCK_TYPES.RECTANGULAR) {
    const { heightDirection } = result;
    return (heightDirection as Vector3)
      .clone()
      .cross(lengthDirection as Vector3)
      .normalize();
  }
};

const getTranslationVectorForNthBar = (
  result:
    | ResultForCreation<STOCK_TYPES.RECTANGULAR>
    | ResultForCreation<STOCK_TYPES.CYLINDRICAL>,
  stockOption: CylindericalStockOptions | RectangularStockOptions,
  n: number,
): Vector3 | undefined => {
  const offsetDir = getOffsetDirectionForNthBar(result);
  if (result.stockType === STOCK_TYPES.CYLINDRICAL) {
    return (offsetDir as Vector3).multiplyScalar(
      (stockOption as CylindericalStockOptions).diameter *
        (1 + VISUALIZATION_GAP_FRACTION) *
        n,
    );
  } else if (result.stockType === STOCK_TYPES.RECTANGULAR) {
    return (offsetDir as Vector3).multiplyScalar(
      (stockOption as RectangularStockOptions).width *
        (1 + VISUALIZATION_GAP_FRACTION) *
        n,
    );
  }
};

const resolveStockVisualization = (
  renderer: Renderer,
  topAssemblyInstance: AssemblyInstance,
): void => {
  // now add root group to renderer group
  topAssemblyInstance.load();
  renderer.group.add(topAssemblyInstance.group);

  // need to update renderer boundbox to prevent clipping when moving around
  renderer.updateBoundBox(renderer.state, [topAssemblyInstance]);

  // also need to update max viewport and relative zoom for scene
  const oldDiagonal = renderer.maxViewport.diagonal;
  const newMaxViewport = ViewPort.getForBoundingBox(
    (renderer.axisAlignedBoundBox as BoxHelper).geometry.boundingBox as Box3,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );
  const newDiagonal = newMaxViewport.diagonal;
  const factor = newDiagonal / oldDiagonal;
  const newZoom = renderer.state.cameraZoom / factor;
  renderer.state.onCameraZoomChange(newZoom);
  renderer.maxViewport = newMaxViewport;
};

type BarVisualizationOptions = {
  option: CylindericalStockOptions | RectangularStockOptions;
  baseTransformWorkpieceUse: Matrix4;
  baseTransformSacrificalUse: Matrix4;
  topAssemblyInstance: AssemblyInstance;
  newAssemblies: Assembly[];
  newAssemblyInstances: AssemblyInstance[];
  newParts: AnyPart[];
  newBodies: AnyBody[];
};

const makeBarOptionVisualization = (
  result:
    | ResultForCreation<STOCK_TYPES.RECTANGULAR>
    | ResultForCreation<STOCK_TYPES.CYLINDRICAL>,
  bodyTransform: Matrix4,
  partsToRender: number,
  optionRenderIndex: number,
  renderer: Renderer,
  {
    option,
    baseTransformWorkpieceUse,
    baseTransformSacrificalUse,
    topAssemblyInstance,
    newAssemblies,
    newAssemblyInstances,
    newParts,
    newBodies,
  }: BarVisualizationOptions,
  //Existing barEndPart, workpiecePart and sacrificalPart (if undefined, they will be created)
  {
    barEndPart: _barEndPart,
    workpiecePart: _workpiecePart,
    sacrificialPart: _sacrificialPart,
  }: {
    barEndPart?: AnyPart;
    workpiecePart?: AnyPart;
    sacrificialPart?: AnyPart;
  },
): {
  barEndPart: AnyPart;
  workpiecePart: AnyPart;
  sacrificialPart: AnyPart;
} => {
  const {
    barEnd,
    buffer,
    key,
    length: partLength,
    lengthDirection,
    name,
    sacrificialWidth,
    stockType,
  } = result;

  // create assembly and assemblyinstance for each option
  const optionAssembly = new Assembly(
    {
      partFileUuid: `${key}${optionRenderIndex}`,
      name: `${name} ${optionRenderIndex}`,
    },
    renderer,
  );
  const optionAssemblyInstance = new AssemblyInstance(
    {
      id: optionAssembly.id,
      name: optionAssembly.name,
      objectIds: [],
      partFileUuid: optionAssembly.id,
      isRoot: false,
    },
    optionAssembly,
    renderer,
    undefined, //no parentProgress
    undefined, //no AbortSignal
    true, // isMaterial flag
  );

  topAssemblyInstance.addChild(optionAssemblyInstance);

  newAssemblies.push(optionAssembly);
  newAssemblyInstances.push(optionAssemblyInstance);

  // create part and body for bar end if there is one
  let barEndPart = _barEndPart as Part<PART_TYPES.STEP>;
  if (barEnd && !isClose(barEnd, 0)) {
    if (!barEndPart) {
      const barEndFaces = createGeometryDataFromGeometries(
        generateBarFaceGeometries(stockType, barEnd, option),
      );
      const barEndGeometry = createStepGeometryFromManualFaces(barEndFaces);
      barEndPart = createStockPart(
        `${name} ${optionRenderIndex} bar end`,
        `${key}${option.id}barEnd`,
        barEndGeometry,
        renderer,
      );

      newParts.push(barEndPart);
    }

    // create barEnd body
    const barEndTranslationVector = new Vector3().copy(
      (lengthDirection as Vector3)
        .clone()
        .multiplyScalar(0 - buffer / 2 - barEnd),
    );
    const barEndTranslationMatrix = new Matrix4().setPosition(
      barEndTranslationVector,
    );
    const baseTransformBarEnd = getBaseTransform(result, barEnd, bodyTransform);

    // if we are on a new bar, we need to offset the barEnd transform
    const transVec = getTranslationVectorForNthBar(
      result,
      option,
      optionRenderIndex,
    );
    const transMatrix = new Matrix4().setPosition(transVec as Vector3);
    baseTransformBarEnd.premultiply(transMatrix);

    const barEndTransform = baseTransformBarEnd
      .clone()
      .premultiply(barEndTranslationMatrix);

    const barEndBody = new Body<PART_TYPES.STEP>(
      {
        id: barEndPart.id,
        name: barEndPart.name,
        partFileUuid: barEndPart.id,
        transform: barEndTransform.toArray(),
        color: BAR_END_COLOR,
        opacity: STOCK_OPACITY,
        isRoot: false,
      },
      barEndPart,
      renderer,
      undefined, //no parentProgress
      undefined, //no AbortSignal
      UNITS.MM,
      true, // isMaterial flag
      { depthWrite: false, side: BackSide },
    );
    barEndBody.load();

    optionAssemblyInstance.addChild(barEndBody);

    newBodies.push(barEndBody);
  }

  // parts for workpiece and sacrificial
  let workpiecePart = _workpiecePart;
  if (!workpiecePart) {
    const workpieceFaces = createGeometryDataFromGeometries(
      generateBarFaceGeometries(stockType, partLength + buffer, option),
    );
    const workpieceGeometry = createStepGeometryFromManualFaces(workpieceFaces);
    workpiecePart = createStockPart(
      `${name} ${optionRenderIndex} workpiece`,
      `${key}${option.id}workpiece`,
      workpieceGeometry,
      renderer,
    );

    newParts.push(workpiecePart);
  }

  let sacrificialPart = _sacrificialPart;
  if (!isClose(sacrificialWidth, 0) && !sacrificialPart) {
    const sacrificialFaces = createGeometryDataFromGeometries(
      generateBarFaceGeometries(stockType, sacrificialWidth, option),
    );
    const sacrificialGeometry =
      createStepGeometryFromManualFaces(sacrificialFaces);
    sacrificialPart = createStockPart(
      `${name} ${optionRenderIndex} sacrifical`,
      `${key}${option.id}sacrificial`,
      sacrificialGeometry,
      renderer,
    );

    newParts.push(sacrificialPart);
  }

  // now for every stock this option makes, create a step body linking to the part with a stepped transform
  for (let i = 0; i < partsToRender; i++) {
    const workpieceTranslationVector = new Vector3().copy(
      (lengthDirection as Vector3)
        .clone()
        .multiplyScalar(partLength * i + buffer * i + sacrificialWidth * i),
    );
    const workpieceTranslationMatrix = new Matrix4().setPosition(
      workpieceTranslationVector,
    );
    const workpieceTransform = baseTransformWorkpieceUse
      .clone()
      .premultiply(workpieceTranslationMatrix);
    // for body disappearing
    // https://stackoverflow.com/questions/11165345/three-js-webgl-transparent-planes-hiding-other-planes-behind-them
    const workpieceBody = new Body<PART_TYPES.STEP>(
      {
        id: `${workpiecePart.id}${i}`,
        name: `${workpiecePart.name} ${i}`,
        partFileUuid: workpiecePart.id,
        transform: workpieceTransform.toArray(),
        color: WORKPIECE_COLOR,
        opacity: STOCK_OPACITY,
        isRoot: false,
      },
      workpiecePart,
      renderer,
      undefined, //no parentProgress
      undefined, //no AbortSignal
      UNITS.MM,
      true, // isMaterial flag
      { depthWrite: false, side: BackSide },
    );
    workpieceBody.load();

    optionAssemblyInstance.addChild(workpieceBody);

    newBodies.push(workpieceBody);

    if (!isClose(sacrificialWidth, 0)) {
      const sacrificialTranslationVector = new Vector3().copy(
        (lengthDirection as Vector3)
          .clone()
          .multiplyScalar(
            partLength * i +
              buffer * i +
              sacrificialWidth * i +
              partLength +
              buffer / 2,
          ),
      );
      const sacrificialTranslationMatrix = new Matrix4().setPosition(
        sacrificialTranslationVector,
      );
      const sacrificialTransform = baseTransformSacrificalUse
        .clone()
        .premultiply(sacrificialTranslationMatrix);

      const sacrificialBody = new Body<PART_TYPES.STEP>(
        {
          id: `${sacrificialPart?.id as string}${i}`,
          name: `${sacrificialPart?.name as string} ${i}`,
          partFileUuid: sacrificialPart?.id,
          transform: sacrificialTransform.toArray(),
          color: SACRIFICIAL_COLOR,
          opacity: STOCK_OPACITY,
          isRoot: false,
        },
        sacrificialPart as AnyPart,
        renderer,
        undefined, //no parentProgress
        undefined, //no AbortSignal
        UNITS.MM,
        true, // isMaterial flag
        { depthWrite: false, side: BackSide },
      );
      sacrificialBody.load();

      optionAssemblyInstance.addChild(sacrificialBody);

      newBodies.push(sacrificialBody);
    }
  }

  // now create the unused part if any unused length
  const unusedLength =
    option.length -
    (partLength + buffer + sacrificialWidth) * partsToRender -
    barEnd;
  if (!isClose(unusedLength, 0)) {
    const unusedFaces = createGeometryDataFromGeometries(
      generateBarFaceGeometries(stockType, unusedLength, option),
    );
    const unusedGeometry = createStepGeometryFromManualFaces(unusedFaces);
    const unusedPart = createStockPart(
      `${name} ${optionRenderIndex} unused`,
      `${key}${option.id}unused`,
      unusedGeometry,
      renderer,
    );

    newParts.push(unusedPart);

    // create unused body
    const unusedTranslationVector = new Vector3().copy(
      (lengthDirection as Vector3)
        .clone()
        .multiplyScalar(
          (partLength + buffer + sacrificialWidth) * partsToRender - buffer / 2,
        ),
    );
    const unusedTranslationMatrix = new Matrix4().setPosition(
      unusedTranslationVector,
    );
    const baseTransformUnused = getBaseTransform(
      result,
      unusedLength,
      bodyTransform,
    );

    // if we are on a new bar, we need to offset the unused transform
    const transVec = getTranslationVectorForNthBar(
      result,
      option,
      optionRenderIndex,
    );
    const transMatrix = new Matrix4().setPosition(transVec as Vector3);
    baseTransformUnused.premultiply(transMatrix);

    const unusedTransform = baseTransformUnused
      .clone()
      .premultiply(unusedTranslationMatrix);
    const unusedBody = new Body(
      {
        id: unusedPart.id,
        name: unusedPart.name,
        partFileUuid: unusedPart.id,
        transform: unusedTransform.toArray(),
        color: UNUSED_COLOR,
        opacity: STOCK_OPACITY,
        isRoot: false,
      },
      unusedPart,
      renderer,
      undefined, //no parentProgress
      undefined, //no AbortSignal
      UNITS.MM,
      true, // isMaterial flag
      { depthWrite: false, side: BackSide },
    );
    unusedBody.load();

    optionAssemblyInstance.addChild(unusedBody);

    newBodies.push(unusedBody);
  }

  optionAssemblyInstance.load();

  return {
    barEndPart: barEndPart,
    workpiecePart: workpiecePart,
    sacrificialPart: sacrificialPart as AnyPart,
  };
};

const createBarVisualization = (
  result:
    | ResultForCreation<STOCK_TYPES.RECTANGULAR>
    | ResultForCreation<STOCK_TYPES.CYLINDRICAL>,
  renderer: Renderer,
  bodyTransform: Matrix4,
) => {
  const {
    key,
    length: partLength,
    name,
    quantity,
    sacrificialWidth,
    stockOptions,
  } = result;
  // create base matrix4 transform
  const baseTransformWorkpiece = getBaseTransform(
    result,
    partLength,
    bodyTransform,
  );
  const baseTransformSacrifical = getBaseTransform(
    result,
    sacrificialWidth,
    bodyTransform,
  );

  // now create a new Assembly for top level
  const topAssembly = new Assembly(
    {
      partFileUuid: key,
      name,
    },
    renderer,
  );
  // now create a new AssemblyInstance for top level
  const topAssemblyInstance = new AssemblyInstance(
    {
      id: topAssembly.id,
      name,
      partFileUuid: topAssembly.id,
      isRoot: true,
    },
    topAssembly,
    renderer,
    undefined, //no parentProgress
    undefined, //no AbortSignal
    true, // isMaterial flag
  );

  const newAssemblies = [topAssembly];
  const newAssemblyInstances = [topAssemblyInstance];
  const newParts: AnyPart[] = [];
  const newBodies: AnyBody[] = [];

  const baseTransformWorkpieceUse = baseTransformWorkpiece.clone();
  const baseTransformSacrificalUse = baseTransformSacrifical.clone();
  let partOutput: {
    barEndPart?: AnyPart;
    workpiecePart?: AnyPart;
    sacrificialPart?: AnyPart;
  } = {};

  // create step geometries manually for stock option pieces
  let optionRenderIndex = 0;
  let trackedCount = 0;
  for (const option of stockOptions) {
    const partsToRender =
      trackedCount + option.makes > quantity
        ? quantity - trackedCount
        : option.makes;

    partOutput = makeBarOptionVisualization(
      result,
      bodyTransform,
      partsToRender,
      optionRenderIndex,
      renderer,
      {
        option,
        baseTransformWorkpieceUse,
        baseTransformSacrificalUse,
        topAssemblyInstance,
        newAssemblies,
        newAssemblyInstances,
        newParts,
        newBodies,
      },
      partOutput,
    );

    trackedCount += partsToRender * option.quantity;

    // offset base transform use to see second bar
    const transVec = getTranslationVectorForNthBar(result, option, 1);
    const transMatrix = new Matrix4().setPosition(transVec as Vector3);
    baseTransformWorkpieceUse.premultiply(transMatrix);
    baseTransformSacrificalUse.premultiply(transMatrix);

    optionRenderIndex += 1;
  }

  // render second visualization of final option to show leftover material if necessary
  if (trackedCount > quantity) {
    const lastOption = stockOptions[stockOptions.length - 1];
    const partsToRenderOnLastBar = lastOption.makes - trackedCount + quantity;
    makeBarOptionVisualization(
      result,
      bodyTransform,
      partsToRenderOnLastBar,
      optionRenderIndex,
      renderer,
      {
        option: lastOption,
        baseTransformWorkpieceUse,
        baseTransformSacrificalUse,
        topAssemblyInstance,
        newAssemblies,
        newAssemblyInstances,
        newParts,
        newBodies,
      },
      partOutput,
    );
  }

  const {
    bodyOrAssemblyInstanceById,
    partOrAssemblyById,
    bodyIdsByPartFileUuid,
    // parts,
    // assemblies,
    bodies,
    assemblyInstances,
  } = renderer.state;
  for (const part of newParts) {
    // parts.push(part);
    partOrAssemblyById.set(part.id as string, part);
  }
  for (const assembly of newAssemblies) {
    // assemblies.push(assembly);
    partOrAssemblyById.set(assembly.id as string, assembly);
  }
  for (const body of newBodies) {
    bodies.push(body);
    bodyOrAssemblyInstanceById.set(body.id as string, body);
    bodyIdsByPartFileUuid.set(body.partUuid as string, body.id as string);
  }
  for (const assembyInstance of newAssemblyInstances) {
    assemblyInstances.push(assembyInstance);
    bodyOrAssemblyInstanceById.set(
      assembyInstance.id as string,
      assembyInstance,
    );
  }

  resolveStockVisualization(renderer, topAssemblyInstance);
};

const makeEdgesDataFromPoints = (
  sheetPoints: [Vector3, ...Vector3[]], //1 or more points
): { edgesData: EdgeJson[]; widthAxis: Vector3; width: number } => {
  const edgesData: EdgeJson[] = [];
  let widthAxis: Vector3 | undefined;
  let width: number | undefined;
  for (let i = 0; i < sheetPoints.length; i++) {
    const p1 = sheetPoints[i];
    let p2;
    if (i === sheetPoints.length - 1) {
      p2 = sheetPoints[0];
    } else {
      p2 = sheetPoints[i + 1];
    }
    const sepVector = new Vector3().subVectors(p2, p1);
    const length = sepVector.length();
    const axis = sepVector.normalize();
    if (i === 0) {
      width = length;
      widthAxis = axis.clone();
    }
    edgesData.push({
      ind: i,
      points: [
        [p1.x, p1.y, p1.z],
        [p2.x, p2.y, p2.z],
      ],
      meta: {
        length,
        type: 'line',
        axis: [axis.x, axis.y, axis.z],
      },
    });
  }
  return { edgesData, widthAxis: widthAxis as Vector3, width: width as number };
};

const getSheetAssemblyId = (
  result: ResultForCreation<STOCK_TYPES.SHEET>,
  sheetPosition: number,
): string => `${result.key}sheet${sheetPosition}`;

const createSheetBasePart = (
  result: ResultForCreation<STOCK_TYPES.SHEET>,
  renderer: Renderer,
): { basePart: AnyPart; isNewPart?: boolean } => {
  let basePart: Part<PART_TYPES.DXF> | undefined;
  let isNewPart: boolean | undefined;
  // first make base step, either from tessellation or a base geometry
  if (result.tessellation && !result.useBaseGeometry && !result.isManual) {
    // case of unfolded sheet metal part without manual part dimensions
    basePart = new Part<PART_TYPES.DXF>(
      {
        partFileUuid: `${result.key}base`,
        name: 'Base Nested Part',
        tess: result.tessellation,
      },
      renderer,
    );
    basePart.load();
    isNewPart = true;
  } else if (result.useBaseGeometry) {
    // case of dxf part where we need to extract a step copy of base part
    const partOrAssemblyById = renderer.state.partOrAssemblyById;
    //The result may identify the part's uuid by different names partUuid, uuid, or partFileUuid!
    //TODO: Can we use a consistent name? Or is there legacy data with these different names?
    basePart = (partOrAssemblyById.get(result.uuid as string) || //use uuid. That's what is set in practice
      partOrAssemblyById.get(result.partUuid as string) ||
      partOrAssemblyById.get(
        result.partFileUuid as string,
      )) as Part<PART_TYPES.DXF>;
    isNewPart = false;
  } else if (result.isManual) {
    // case where user inputted manual dimensions for part, and we manually construct geometry
    basePart = new Part<PART_TYPES.DXF>(
      {
        partFileUuid: `${result.key}base`,
        name: 'Base Nested Part',
        tess: {
          Type: PART_TYPES.DXF,
          Edges: makeEdgesDataFromPoints(result.partPoints).edgesData,
          Faces: [],
          Vertexes: [],
        },
      },
      renderer,
    );
    basePart.load();
    isNewPart = true;
  }

  return { basePart: basePart as AnyPart, isNewPart };
};

const createSheetStockPart = (
  result: ResultForCreation<STOCK_TYPES.SHEET>,
  renderer: Renderer,
): { sheetPart: Part<PART_TYPES.DXF>; widthAxis?: Vector3; width?: number } => {
  const {
    edgesData: sheetEdgesData,
    widthAxis,
    width,
  } = makeEdgesDataFromPoints(result.sheetPoints);

  const sheetPart = new Part<PART_TYPES.DXF>(
    {
      partFileUuid: `${result.key}sheet`,
      name: 'Sheet',
      tess: {
        Type: PART_TYPES.DXF,
        Edges: sheetEdgesData,
        Faces: [],
        Vertexes: [],
      },
    },
    renderer,
  );
  sheetPart.load();

  return { sheetPart, width, widthAxis };
};

const SHEETstockCreateParts = (
  renderer: Renderer,
  result: ResultForCreation<STOCK_TYPES.SHEET>,
  allVisible: Iterable<string>,
): void => {
  const {
    bodyOrAssemblyInstanceById,
    partOrAssemblyById,
    // parts,
    // assemblies,
    assemblyInstances,
  } = renderer.state;
  setVisibilityForObjectIds(renderer.state, allVisible);

  // now create a new Assembly for top level
  const topAssembly = new Assembly(
    {
      partFileUuid: result.key,
      name: result.key,
    },
    renderer,
  );
  // now create a new AssemblyInstance for top level
  const topAssemblyInstance = new AssemblyInstance(
    {
      id: topAssembly.id,
      name: result.key,
      partFileUuid: topAssembly.id,
      isRoot: true,
    },
    topAssembly,
    renderer,
    undefined, //no parentProgress
    undefined, //no AbortSignal
    true, // isMaterial flag
  );
  const { basePart, isNewPart } = createSheetBasePart(result, renderer);
  const { sheetPart } = createSheetStockPart(result, renderer);

  renderer.group.add(topAssemblyInstance.group);

  if (isNewPart) {
    // parts.push(basePart);
    partOrAssemblyById.set(basePart.id as string, basePart);
  }
  // parts.push(sheetPart);
  partOrAssemblyById.set(sheetPart.id as string, sheetPart);

  //Note: Previously didn't add topAssembly or topAssemblyInstance (seems like it was oversight)
  // assemblies.push(topAssembly);
  partOrAssemblyById.set(topAssembly.id as string, topAssembly);
  assemblyInstances.push(topAssemblyInstance);
  bodyOrAssemblyInstanceById.set(
    topAssemblyInstance.id as string,
    topAssemblyInstance,
  );
};

const SHEETcreateSheet = (
  renderer: Renderer,
  result: ResultForCreation<STOCK_TYPES.SHEET>,
  sheetPosition: number,
  widthAxis: Vector3,
  width: number,
): void => {
  // create assembly and assemblyinstance for each sheet
  const sheetAssembly = new Assembly(
    {
      partFileUuid: getSheetAssemblyId(result, sheetPosition),
      name: getSheetAssemblyId(result, sheetPosition),
    },
    renderer,
  );
  const sheetAssemblyInstance = new AssemblyInstance(
    {
      id: sheetAssembly.id,
      name: sheetAssembly.name,
      partFileUuid: sheetAssembly.id,
      isRoot: false,
    },
    sheetAssembly,
    renderer,
    undefined, //no parentProgress
    undefined, //no AbortSignal
    true, // isMaterial flag
  );

  const {
    bodyOrAssemblyInstanceById,
    partOrAssemblyById,
    bodyIdsByPartFileUuid,
    // assemblies,
    bodies,
    assemblyInstances,
  } = renderer.state;

  const topAssemblyInstance = bodyOrAssemblyInstanceById.get(
    result.key,
  ) as AssemblyInstance;

  topAssemblyInstance.addChild(sheetAssemblyInstance);

  const sheetPart = partOrAssemblyById.get(
    `${result.key}sheet`,
  ) as Part<PART_TYPES.DXF>;

  // establish base transform for sheet count based on sheet point directions
  const transVec = widthAxis
    .clone()
    .multiplyScalar(sheetPosition * (1 + VISUALIZATION_GAP_FRACTION) * width);
  const baseTransform = new Matrix4().setPosition(transVec);

  // create body for sheet
  const sheetBody = new Body<PART_TYPES.DXF>(
    {
      id: `${sheetPart.id as string}${sheetPosition}`,
      name: `${sheetPart.name} ${sheetPosition}`,
      partFileUuid: sheetPart.id,
      transform: baseTransform.toArray(),
      isRoot: false,
    },
    sheetPart,
    renderer,
    undefined, //no parentProgress
    undefined, //no AbortSignal
    UNITS.MM, // figure out units
    true, // isMaterial flag
  );
  sheetBody.load();

  sheetAssemblyInstance.addChild(sheetBody);
  topAssemblyInstance.load(); //necessary to set boundBox for camera

  // now update camera to zoom out to see all results of this sheet
  // and update renderer wrapping bounding box to prevent clipping
  resolveStockVisualization(renderer, topAssemblyInstance);

  const finalViewport = setUpCameraAndControls(
    topAssemblyInstance.boundBox as BoxHelper,
    renderer.axisAlignedBoundBox as BoxHelper,
    renderer,
  );

  const d1 = renderer.maxViewport.diagonal;
  const d2 = finalViewport.diagonal;

  renderer.state.onCameraZoomChange(d1 / d2);
  renderer.state.onOrthoViewChange(finalViewport);

  //Add new sheetAssembly, sheetAssemblyInstance and sheetBody (sheetPart is already added)
  // assemblies.push(sheetAssembly);
  partOrAssemblyById.set(sheetAssembly.id as string, sheetAssembly);
  bodies.push(sheetBody);
  assemblyInstances.push(sheetAssemblyInstance);
  bodyOrAssemblyInstanceById.set(sheetBody.id as string, sheetBody);
  bodyOrAssemblyInstanceById.set(
    sheetAssemblyInstance.id as string,
    sheetAssemblyInstance,
  );
  bodyIdsByPartFileUuid.set(
    sheetBody.partUuid as string,
    sheetBody.id as string,
  );
};

const SHEETcreateBodyInSheet = (
  renderer: Renderer,
  result: ResultForCreation<STOCK_TYPES.SHEET>,
  sheetPosition: number,
  transformIndex: number,
  widthAxis: Vector3,
  width: number,
): void => {
  const transVec = widthAxis
    .clone()
    .multiplyScalar(sheetPosition * (1 + VISUALIZATION_GAP_FRACTION) * width);
  const baseTransform = new Matrix4().setPosition(transVec);

  // create new body for location and apply transforms
  const transform = result.transforms[transformIndex];
  const transformUse = transform.clone().premultiply(baseTransform);

  const {
    bodyOrAssemblyInstanceById,
    partOrAssemblyById,
    bodyIdsByPartFileUuid,
    bodies,
  } = renderer.state;

  const basePart = (
    result.useBaseGeometry
      ? partOrAssemblyById.get(result.uuid as string) || //use uuid. That's what is set in practice
        partOrAssemblyById.get(result.partUuid as string) ||
        partOrAssemblyById.get(result.partFileUuid as string)
      : partOrAssemblyById.get(`${result.key}base`)
  ) as AnyPart;

  const nestBody = new Body<PART_TYPES.DXF>(
    {
      id: `${result.key}body${sheetPosition}${transformIndex}`,
      name: `${result.key}body${sheetPosition}${transformIndex}`,
      partFileUuid: basePart.id as string,
      transform: transformUse.toArray(),
      isRoot: false,
    },
    basePart,
    renderer,
    undefined, //no parentProgress
    undefined, //no AbortSignal
    UNITS.MM, // TODO: figure out units
    true, // isMaterialFlag
  );
  nestBody.load();

  const sheetAssemblyInstance = bodyOrAssemblyInstanceById.get(
    getSheetAssemblyId(result, sheetPosition),
  ) as AssemblyInstance;

  sheetAssemblyInstance.addChild(nestBody);

  //Add new nestBody, (basePart and sheetAssemblyInstance are already added)
  bodies.push(nestBody);
  bodyOrAssemblyInstanceById.set(nestBody.id as string, nestBody);
  bodyIdsByPartFileUuid.set(nestBody.partUuid as string, nestBody.id as string);
};

//loadSheetStockVisualization
//* used to create parts, assemblies, bodies, assemblyInstances for visualizing the nested sheetmetal parts
//* Never used loadingProgress (previously loading was estimated/tracked, but in production it is not used)
//* Has `await wait()` strategically placed so that the loading time does not block UI
export const loadSheetStockVisualization = async (
  rawResult: RawResultForCreation<STOCK_TYPES.SHEET>,
  renderer: Renderer,
  allVisible: Iterable<string>,
): Promise<void> => {
  const resultKey = rawResult.key;
  try {
    const { bodyOrAssemblyInstanceById } = renderer.state;
    // find the body to superimpose the result onto
    // NOTE: we dont use this in loading instructions as for right now we cant have multiple dxf instances
    // in an assembly
    if (!bodyOrAssemblyInstanceById.has(rawResult.bodyId)) {
      console.warn('Could not find body for sheet visualization');
      return;
    }

    const result = prepareResultForCreation(
      rawResult,
      renderer.state.nativeUnits,
    );

    const { width, widthAxis } = makeEdgesDataFromPoints(result.sheetPoints);

    SHEETstockCreateParts(renderer, result, allVisible);
    await wait();
    SHEETcreateSheet(renderer, result, 0, widthAxis, width);
    await wait();
    renderer.requestRenderIfNotRequested();

    const leftovers = result.quantity % result.partsPerSheet;
    const evenSheet = leftovers === 0;
    const firstSheetCount =
      result.sheetCount > 1
        ? result.partsPerSheet
        : evenSheet
        ? result.partsPerSheet
        : result.quantity;
    const secondSheetCount = result.sheetCount === 1 ? 0 : leftovers;

    for (
      let transformIndex = 0;
      transformIndex < firstSheetCount;
      transformIndex++
    ) {
      await wait();
      SHEETcreateBodyInSheet(
        renderer,
        result,
        0,
        transformIndex,
        widthAxis,
        width,
      );
      await wait();
      if (transformIndex % 10 === 0) {
        renderer.requestRenderIfNotRequested();
      }
    }

    if (result.sheetCount > 1 && secondSheetCount) {
      await wait();
      SHEETcreateSheet(renderer, result, 1, widthAxis, width);
      await wait();

      for (
        let transformIndex = 0;
        transformIndex < secondSheetCount;
        transformIndex++
      ) {
        await wait();
        SHEETcreateBodyInSheet(
          renderer,
          result,
          1,
          transformIndex,
          widthAxis,
          width,
        );
        await wait();
        if (transformIndex % 10 === 0) {
          renderer.requestRenderIfNotRequested();
        }
        await wait();
      }
    }

    renderer.requestRenderIfNotRequested();

    //stock was created earlier, but waiting until the nesting is complete because
    //if subsequent creation work throws error, we don't want to call stockCreationFailed()
    //after stockCreationSuccess()
    renderer.state.stockCreationSuccess({ resultKey });
    await wait();
    renderer.state.onNestingCreationFinish({ result });
  } catch (error) {
    console.error('Stock creation failed', error);
    renderer.state.stockCreationFailed({ resultKey });
  }
};

//handleUpdateCreateMaterialStock is for STOCK_TYPES CYLINDERICAL and RECTANGULAR
//* It is not for sheetmetal
export const handleUpdateCreateMaterialStock = (
  renderer: Renderer,
  nextState: RendererState,
) => {
  if (
    renderer.state.materialStockResults.length <
    nextState.materialStockResults.length
  ) {
    // collect the bodies that have not been acknowledged from next results
    const { materialStockResults: nextResults, nativeUnits } = nextState;
    const { partOrAssemblyById, bodyOrAssemblyInstanceById } = nextState;

    // partOrAssemblyById.set(sheetAssembly.uuid, sheetAssembly);

    const resultsToBuild = nextResults.filter(
      (result) =>
        !partOrAssemblyById.has(result.key) &&
        [STOCK_TYPES.CYLINDRICAL, STOCK_TYPES.RECTANGULAR].includes(
          result.stockType,
        ),
    );

    for (const result of resultsToBuild) {
      // find the body to superimpose the result onto
      const body = bodyOrAssemblyInstanceById.get(result.bodyId);

      const convertedResult = prepareResultForCreation(result, nativeUnits);

      if (!body) {
        console.warn('Did not find body to map to stock visualization');
        nextState.stockCreationFailed({ resultKey: convertedResult.key });
        continue;
      }

      if (convertedResult.stockType === STOCK_TYPES.RECTANGULAR) {
        if (
          !(convertedResult as ResultForCreation<STOCK_TYPES.RECTANGULAR>)
            .center
        ) {
          // query the existing bodies and assemblyInstances and extract center based on bounding box
          // in this case, we know the length vector will be one of the positive unit vectors, so we can
          // just get midpoint of bounding box and then take the projection
          const boundBox = prepareBoundBoxForBodies([body]).geometry
            .boundingBox as Box3;
          const { x, y, z } = (
            convertedResult as ResultForCreation<STOCK_TYPES.RECTANGULAR>
          ).lengthDirection as VectorJson;
          const lengthDir = new Vector3(x, y, z);
          const diagonal = new Vector3().subVectors(boundBox.max, boundBox.min);
          const distToCenterFromMid = diagonal.dot(lengthDir) / 2;
          const mid = getMidpoint(boundBox.min, boundBox.max);
          (
            convertedResult as ResultForCreation<STOCK_TYPES.RECTANGULAR>
          ).center = new Vector3().addVectors(
            mid,
            lengthDir.multiplyScalar(-distToCenterFromMid),
          );
        }

        createBarVisualization(
          convertedResult as ResultForCreation<STOCK_TYPES.RECTANGULAR>,
          renderer,
          body.transform,
        );

        // acknowledge success
        nextState.stockCreationSuccess({ resultKey: convertedResult.key });
      } else if (convertedResult.stockType === STOCK_TYPES.CYLINDRICAL) {
        createBarVisualization(
          convertedResult as ResultForCreation<STOCK_TYPES.CYLINDRICAL>,
          renderer,
          body.transform,
        );

        // acknowledge success
        nextState.stockCreationSuccess({ resultKey: convertedResult.key });
      } else {
        console.warn(`unsupported stock type: ${convertedResult.stockType}`);
        nextState.stockCreationFailed({ resultKey: convertedResult.key });
      }
    }
  }
};
